import { Formik, FormikActions, FormikErrors } from 'formik';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { compose } from 'recompose';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import { useSubDomainInfo_subDomain } from 'LoggedOut/SignUp/__generated__/useSubDomainInfo';
import { usePractitionerCountries } from 'Book/shared';
import { useUserEntitlements } from 'hooks/useUserEntitlements';
import {
  Alert,
  ButtonSize,
  showToast,
  SingleSelect,
  TextInput,
  Toast,
} from '@unmind/design-system';
import styled from 'styled-components';
import { UserFeature } from '__generated__/globalTypes';
import { validateEmail } from 'Shared/Form/Formik';
import {
  useCountries,
  useUserCurrentLocation,
  useSetUserSelectedLocation,
  mapAndFilterCountries,
  ALL_OTHER_COUNTRIES_CODE,
} from 'Shared/location';
import { useSubdomainInfo } from '../../LoggedOut/SignUp/useSubdomainInfo';
import { tracking } from '../../App/Tracking';
import { LocalGeneralError } from '../../Shared/AlertView';
import { Errors } from '../../Shared/Errors';
import { GridItem } from '../../Shared/Grid';
import LoadingIndicator, {
  withLoadingIndicator,
} from '../../Shared/LoadingIndicator';
import { isEmployee, isPlusOne } from '../../utils';
import withUpdateUser, { UpdateUserInput } from '../withUpdateUser';
import {
  useUser,
  useSubdomainDivisions,
  useUserLocation,
  countryRequiresState,
  CANADA,
} from '../hooks';
import { changeUserDetails, getChangedFieldKeys } from './helper';
import * as S from './Profile.styles';

export interface ProfileProps {
  confirmEmailUpdateSuccess: boolean;
  confirmEmailUpdateError: boolean;
  updateUser(inputProps: UpdateUserInput): Promise<{
    success: boolean;
    error?: Errors;
  }>;
}

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

interface ProfileOutProps {
  confirmEmailUpdateSuccess: boolean;
  confirmEmailUpdateError: boolean;
}

export interface FormFields {
  [property: string]: boolean | string | undefined;
  firstName: string;
  lastName: string;
  emailAddress: string;
  initialEmailAddress: string;
  location?: string;
  department?: string;
  country?: string;
  state?: string;
  helpLocation?: string;
}

const checkIsEmailOrganisation = (
  groupFileFields: useSubDomainInfo_subDomain['groupFileFields'] | undefined,
) => {
  if (Array.isArray(groupFileFields) && groupFileFields.length > 0) {
    return groupFileFields.some(
      groupFileField => groupFileField?.fileMappingField === 'email',
    );
  }

  return true;
};

const isAnyLoading = (loadingFlags: (boolean | undefined)[]) =>
  loadingFlags.some(loading => loading);

export const Profile: React.FC<ProfileProps> = ({
  confirmEmailUpdateSuccess,
  confirmEmailUpdateError,
  updateUser,
}) => {
  const {
    firstName = '',
    lastName = '',
    email = '',
    locationId,
    departmentId,
    userType,
    loading: loadingUser,
    error: userLoadingError,
    features = [],
  } = useUser();
  const subdomain = getSubdomainFromUrl();
  const {
    locations,
    departments,
    loading: loadingSubdomainDivisions,
  } = useSubdomainDivisions(subdomain);
  const formikRef = useRef<Formik<FormFields>>(null);
  const { t: translate } = useTranslation('account');

  //for now all users authenticated via auth0 are SSO users, we will revist this after auth0 migration work
  const { isAuthenticated: isAuthenticatedViaAuth0, isLoading: loadingAuth0 } =
    useAuth0();
  const { groupFileFields, loading: loadingSubdomainInfo } = useSubdomainInfo({
    subdomain,
  });

  const {
    userLocationData,
    hasValidLocationData,
    requiresState,
    loading: loadingLocationData,
  } = useUserLocation();

  const {
    countryData,
    getStateData,
    loading: loadingPractitionerCountry,
  } = usePractitionerCountries();

  const { entitlements, loading: loadingUserEntitlements } =
    useUserEntitlements();

  const {
    data: countriesData,
    loading: loadingCountries,
    error: countriesError,
  } = useCountries();
  const {
    data: currentHelpLocationData,
    loading: loadingCurrentHelpLocation,
    error: currentHelpLocationError,
  } = useUserCurrentLocation();
  const { setLocation: setHelpLocation } = useSetUserSelectedLocation({
    source: 'account',
  });

  // `useUserCurrentLocation` is never refetched here and the mutation payload
  // can't update its cache, so we track the last saved help location locally.
  // Comparing against this (rather than the never-refreshing server value)
  // stops repeat saves from re-firing the mutation + tracking event.
  const [savedHelpLocation, setSavedHelpLocation] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (confirmEmailUpdateSuccess) {
      tracking.track('email-reset-confirmed');
      showToast(translate('profile.messages.confirm_email_update_success'));
    }

    if (confirmEmailUpdateError) {
      showToast(translate('profile.messages.confirm_email_update_error'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    isAnyLoading([
      loadingSubdomainInfo,
      loadingAuth0,
      loadingSubdomainDivisions,
      loadingUser,
      loadingLocationData,
      loadingPractitionerCountry,
      loadingUserEntitlements,
      loadingCountries,
      loadingCurrentHelpLocation,
    ])
  ) {
    return <LoadingIndicator />;
  }

  if (userLoadingError || !userType) {
    return <LocalGeneralError />;
  }

  // A countries / current-location fetch failure is scoped to an inline error
  // beside the help-location selector (below) rather than blanking the whole
  // page, so the rest of the form stays usable.
  const helpLocationError = Boolean(countriesError || currentHelpLocationError);

  const isEmailOrganisation = checkIsEmailOrganisation(groupFileFields);

  const isPlusOneUser = isPlusOne(userType);

  const countryOptions = mapAndFilterCountries(countriesData?.countries || [], [
    ALL_OTHER_COUNTRIES_CODE,
  ]);

  const serverHelpLocation =
    currentHelpLocationData?.user?.selectedCurrentLocation?.value;
  // Prefer the last value we saved this session; fall back to the server value.
  const previousHelpLocation = savedHelpLocation ?? serverHelpLocation;

  const initialValues: FormFields = {
    firstName,
    lastName,
    emailAddress: email,
    initialEmailAddress: email,
    ...(locationId ? { location: locationId } : undefined),
    ...(departmentId ? { department: departmentId } : undefined),
    ...(hasValidLocationData && userLocationData?.user?.country?.value
      ? { country: userLocationData?.user?.country?.value }
      : undefined),
    ...(hasValidLocationData &&
    requiresState &&
    userLocationData?.user?.state?.value
      ? { state: userLocationData?.user?.state?.value }
      : undefined),
    ...(serverHelpLocation ? { helpLocation: serverHelpLocation } : undefined),
  };

  const saveUserDetails = async (fields: FormFields) =>
    // `helpLocation` is dropped by `changeUserDetails` (it destructures the
    // known user-detail fields), so it's safe to spread the whole form here.
    changeUserDetails({
      updateUser,
      ...fields,
      state:
        fields.country && countryRequiresState(fields.country)
          ? fields.state
          : null,
    });

  const saveHelpLocation = async (countryCode: string) => {
    const selectedCountry = countryOptions.find(
      option => option.value === countryCode,
    );
    const { success } = await setHelpLocation(
      countryCode,
      selectedCountry?.label,
    );

    if (success) {
      setSavedHelpLocation(countryCode);
    }

    return success;
  };

  // Resolves the toast(s) for a submit. When both sections saved successfully a
  // single combined success toast is shown; otherwise each changed section
  // reports its own outcome, so a mixed result surfaces both.
  const getSaveToasts = ({
    userDetailsChanged,
    userDetailsResponse,
    helpLocationChanged,
    helpLocationSuccess,
    emailAddress,
  }: {
    userDetailsChanged: boolean;
    userDetailsResponse: { success: boolean; emailAddressUpdate?: boolean };
    helpLocationChanged: boolean;
    helpLocationSuccess: boolean;
    emailAddress: string;
  }): string[] => {
    const emailUpdateToast = translate(
      'profile.messages.email_update_success',
      { email: emailAddress },
    );
    const successToast = userDetailsResponse.emailAddressUpdate
      ? emailUpdateToast
      : translate('profile.messages.default_success');

    if (
      userDetailsChanged &&
      userDetailsResponse.success &&
      helpLocationChanged &&
      helpLocationSuccess
    ) {
      return [successToast];
    }

    const toasts: string[] = [];

    if (userDetailsChanged) {
      toasts.push(
        userDetailsResponse.success
          ? successToast
          : translate('profile.messages.failed'),
      );
    }

    if (helpLocationChanged) {
      toasts.push(
        translate(
          helpLocationSuccess
            ? 'profile.messages.help_location_success'
            : 'profile.messages.help_location_failed',
        ),
      );
    }

    return toasts;
  };

  const onSubmit = async (
    formFields: FormFields,
    actions: Pick<
      FormikActions<FormFields>,
      'setStatus' | 'setSubmitting' | 'resetForm'
    >,
  ) => {
    actions.setSubmitting(true);
    actions.setStatus('');

    const { helpLocation } = formFields;

    // helpLocation is excluded from the user-detail diff: it's saved via a
    // separate mutation (so it must not be sent to updateUser) and its change
    // is detected via `helpLocationChanged` against a baseline that, unlike the
    // rest of the form, never refreshes from the server.
    const changedUserDetailFields = getChangedFieldKeys(
      initialValues,
      formFields,
      ['helpLocation'],
    );
    const userDetailsChanged = changedUserDetailFields.length > 0;
    const helpLocationChanged =
      Boolean(helpLocation) && helpLocation !== previousHelpLocation;

    // Each save is skipped when its section is untouched - this avoids a
    // redundant updateUser call (and an empty `settings-changes-saved` event)
    // when only the help location changed, and vice versa.
    const userDetailsResponse = userDetailsChanged
      ? await saveUserDetails(formFields)
      : { success: true, emailAddressUpdate: false };
    const helpLocationSuccess = helpLocationChanged
      ? await saveHelpLocation(helpLocation as string)
      : true;

    if (userDetailsResponse.success && userDetailsResponse.emailAddressUpdate) {
      tracking.track('email-reset-request');
    }

    const userDetailsSaved = userDetailsChanged && userDetailsResponse.success;
    const helpLocationSaved = helpLocationChanged && helpLocationSuccess;

    // Track whatever saved successfully, even if the other section failed -
    // `settingsField` lists only the fields that actually persisted. A
    // help-location change also emits its own `user_selected_location_updated`
    // event from within the hook.
    if (userDetailsSaved || helpLocationSaved) {
      tracking.track('settings-changes-saved', {
        settingsField: [
          ...(userDetailsSaved ? changedUserDetailFields : []),
          ...(helpLocationSaved ? ['helpLocation'] : []),
        ],
      });
    }

    const overallSuccess = userDetailsResponse.success && helpLocationSuccess;

    // Reset only on full success, so a failed section leaves the form dirty and
    // the "Save changes" button enabled for retry.
    if (overallSuccess) {
      actions.resetForm(formFields);
    }

    const toasts = getSaveToasts({
      userDetailsChanged,
      userDetailsResponse,
      helpLocationChanged,
      helpLocationSuccess,
      emailAddress: formFields.emailAddress,
    });

    // Not `.forEach(showToast)`: forEach would pass the array index as
    // showToast's `duration` argument.
    for (const message of toasts) {
      showToast(message);
    }
  };

  const validateForm = (formValues: FormFields) => {
    const errors: FormikErrors<FormFields> = {};

    if (!formValues.firstName) {
      errors.firstName = translate('profile.validation.first_name');
    }
    if (!formValues.lastName) {
      errors.lastName = translate('profile.validation.last_name');
    }
    if (!formValues.emailAddress || validateEmail(formValues.emailAddress)) {
      errors.emailAddress = translate('profile.validation.email');
    }
    if (
      formValues.country &&
      countryRequiresState(formValues.country) &&
      !formValues.state
    ) {
      errors.state =
        formValues.country === CANADA
          ? translate('profile.validation.province')
          : translate('profile.validation.state');
    }

    return errors;
  };

  const locationDepartmentAreEditable = features.includes(
    UserFeature.DEPARTMENT_LOCATION_SELECTION,
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      ref={formikRef}
      validateOnBlur
      validate={validateForm}
    >
      {({ dirty, isSubmitting, isValid, errors, values }) => (
        <>
          <Toast />
          <S.StyledGrid>
            <InputContainer>
              <TextInput
                id="firstName"
                name="firstName"
                hasError={Boolean(errors.firstName)}
                errorText={errors.firstName}
                additionalText={{
                  label: translate(`profile.first_name_input_label`),
                }}
                onBlur={() => {
                  formikRef.current?.setFieldTouched('firstName', true);
                }}
                value={values.firstName}
                onChange={event => {
                  formikRef.current?.setFieldValue(
                    'firstName',
                    event.target.value,
                  );
                }}
              />
            </InputContainer>
            <InputContainer>
              <TextInput
                id="lastName"
                name="lastName"
                hasError={Boolean(errors.lastName)}
                errorText={errors.lastName}
                additionalText={{
                  label: translate(`profile.last_name_input_label`),
                }}
                onBlur={() => {
                  formikRef.current?.setFieldTouched('lastName', true);
                }}
                value={values.lastName}
                onChange={event => {
                  formikRef.current?.setFieldValue(
                    'lastName',
                    event.target.value,
                  );
                }}
              />
            </InputContainer>
            <InputContainer>
              <TextInput
                onChange={event => {
                  formikRef.current?.setFieldValue(
                    'emailAddress',
                    event.target.value,
                  );
                }}
                hasError={Boolean(errors.emailAddress)}
                errorText={errors.emailAddress}
                id="emailAddress"
                onBlur={() => {
                  formikRef.current?.setFieldTouched('emailAddress', true);
                }}
                name="emailAddress"
                value={values.emailAddress}
                disabled={
                  isPlusOneUser ||
                  (isAuthenticatedViaAuth0 && isEmailOrganisation)
                }
                additionalText={{
                  label: translate(`profile.email_input_label`),
                  helpText: isPlusOneUser
                    ? translate('profile.plus_one_user.email_tooltip')
                    : '',
                }}
              />
            </InputContainer>
            {isEmployee(userType) && locationDepartmentAreEditable ? (
              <Fragment>
                {locations && locations.length > 0 && (
                  <SingleSelect
                    id="location"
                    placeholder={translate(
                      `profile.location_select_placeholder`,
                    )}
                    additionalText={{
                      label: translate(`profile.location_select_label`),
                    }}
                    onChange={value =>
                      formikRef.current?.setFieldValue('location', value)
                    }
                    selectedValue={values.location}
                    options={locations.map(({ name, id }) => ({
                      label: name,
                      value: id,
                    }))}
                  />
                )}
                {departments && departments.length > 0 && (
                  <SingleSelect
                    id="department"
                    additionalText={{
                      label: translate(`profile.department_select_placeholder`),
                    }}
                    selectedValue={values.department}
                    placeholder={translate(
                      `profile.department_select_placeholder`,
                    )}
                    onChange={value =>
                      formikRef.current?.setFieldValue('department', value)
                    }
                    options={departments.map(({ name, id }) => ({
                      label: name,
                      value: id,
                    }))}
                  />
                )}
              </Fragment>
            ) : (
              <GridItem fullWidth>
                <S.SectionSeparator />
              </GridItem>
            )}

            {entitlements.unmindTalk ? (
              <Fragment>
                {countryData.length > 0 && (
                  <SingleSelect
                    id="country"
                    additionalText={{
                      label: translate(`profile.country_select_label`),
                    }}
                    placeholder={translate(
                      `profile.country_select_placeholder`,
                    )}
                    selectedValue={values.country}
                    options={countryData}
                    onChange={value => {
                      formikRef.current?.setFieldValue('country', value);
                    }}
                  />
                )}

                {values.country && countryRequiresState(values.country) ? (
                  <SingleSelect
                    id="state"
                    additionalText={{
                      label: translate(
                        `profile.${
                          values.country === CANADA ? 'province' : 'state'
                        }_select_label`,
                      ),
                    }}
                    placeholder={translate(
                      `profile.${
                        values.country === CANADA ? 'province' : 'state'
                      }_select_placeholder`,
                    )}
                    selectedValue={values.state}
                    options={getStateData(values.country)}
                    onChange={value => {
                      formikRef.current?.setFieldValue('state', value);
                    }}
                  />
                ) : null}
              </Fragment>
            ) : null}

            {helpLocationError ? (
              <Alert variant="error" size="small">
                {translate('profile.help_location_error')}
              </Alert>
            ) : (
              countryOptions.length > 0 && (
                <SingleSelect
                  id="helpLocation"
                  additionalText={{
                    label: translate('profile.help_location_select_label'),
                  }}
                  placeholder={translate(
                    'profile.help_location_select_placeholder',
                  )}
                  selectedValue={values.helpLocation}
                  options={countryOptions}
                  onChange={value => {
                    formikRef.current?.setFieldValue('helpLocation', value);
                  }}
                />
              )
            )}
          </S.StyledGrid>
          <S.SubmitButton
            variant="primary"
            disabled={!dirty || !isValid}
            ariaLabel={translate('profile.save_button.a11y_label')}
            label={translate('profile.save_button.label')}
            loading={isSubmitting}
            size={ButtonSize.medium}
            type="submit"
            onClick={() => {
              void formikRef.current?.submitForm();
            }}
          />
        </>
      )}
    </Formik>
  );
};

export default compose<ProfileProps, ProfileOutProps>(
  withLoadingIndicator,
  withUpdateUser,
)(Profile);
